import { TripComponent } from "@shared/schema";
import { 
  PlaneIcon, 
  BuildingIcon, 
  SmileIcon, 
  TruckIcon, 
  UtensilsIcon, 
  FileIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MoreVertical,
  Sparkles,
  Trash2,
  RefreshCw,
  ArrowDownRightIcon
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";

interface ItineraryItemProps {
  component: TripComponent & { _isContinuation?: boolean };
  onClick: () => void;
  isMultiDay?: boolean;
  isLastDay?: boolean;
  expanded?: boolean;
  onExpand?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onRegenerate?: () => void;
  // If true, clicking will use onClick (for popup) instead of expand/collapse
  usePopupOnClick?: boolean;
  // For items that are continued from a previous day
  isContinuation?: boolean;
}

export default function ItineraryItem({ 
  component, 
  onClick, 
  isMultiDay = false, 
  isLastDay = false,
  expanded = false,
  onExpand,
  onEdit,
  onDelete,
  onRegenerate,
  usePopupOnClick = false,
  isContinuation = false
}: ItineraryItemProps) {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const getComponentStyles = () => {
    switch (component.type) {
      case "flight":
        return { 
          borderClass: "component-flight border-l-4 border-l-gradient-orange", 
          badgeClass: "bg-gradient-to-r from-orange-400 to-amber-500 text-white",
          bgClass: "bg-gradient-to-r from-orange-50 to-amber-50",
          iconClass: "text-white",
          icon: <PlaneIcon className="h-4 w-4" />,
          label: "Flight" 
        };
      case "hotel":
        return { 
          borderClass: "component-hotel border-l-4 border-l-gradient-purple", 
          badgeClass: "bg-gradient-to-r from-indigo-500 to-purple-500 text-white",
          bgClass: "bg-gradient-to-r from-indigo-50 to-purple-50",
          iconClass: "text-white",
          icon: <BuildingIcon className="h-4 w-4" />,
          label: "Hotel" 
        };
      case "activity":
        return { 
          borderClass: "component-activity border-l-4 border-l-gradient-amber", 
          badgeClass: "bg-gradient-to-r from-amber-400 to-orange-400 text-white",
          bgClass: "bg-gradient-to-r from-amber-50 to-orange-50",
          iconClass: "text-white",
          icon: <SmileIcon className="h-4 w-4" />,
          label: "Activity" 
        };
      case "transport":
        return { 
          borderClass: "component-transport border-l-4 border-l-gradient-slate", 
          badgeClass: "bg-gradient-to-r from-slate-500 to-slate-600 text-white",
          bgClass: "bg-gradient-to-r from-slate-100 to-slate-200",
          iconClass: "text-white",
          icon: <TruckIcon className="h-4 w-4" />,
          label: "Transport" 
        };
      case "food":
        return { 
          borderClass: "component-food border-l-4 border-l-gradient-rose", 
          badgeClass: "bg-gradient-to-r from-rose-400 to-pink-500 text-white",
          bgClass: "bg-gradient-to-r from-rose-50 to-pink-50",
          iconClass: "text-white",
          icon: <UtensilsIcon className="h-4 w-4" />,
          label: "Meal" 
        };
      default:
        return { 
          borderClass: "border-l-4 border-l-gradient-sky", 
          badgeClass: "bg-gradient-to-r from-sky-400 to-blue-500 text-white",
          bgClass: "bg-gradient-to-r from-sky-50 to-blue-50",
          iconClass: "text-white",
          icon: <FileIcon className="h-4 w-4" />,
          label: "Other" 
        };
    }
  };

  const { borderClass, badgeClass, bgClass, iconClass, icon, label } = getComponentStyles();

  const getTimeDisplay = () => {
    if (!component.startTime) return "";
    
    if (component.endTime) {
      return `${component.startTime} - ${component.endTime}`;
    }
    
    if (isMultiDay) {
      return `${component.startTime} →`;
    }
    
    if (component.endDate) {
      return `→ ${component.startTime}`;
    }
    
    return component.startTime;
  };
  
  const formatDate = (date: Date) => {
    return format(date, "EEEE, MMMM d, yyyy");
  };
  
  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onExpand) {
      onExpand();
    } else {
      setIsExpanded(!isExpanded);
    }
  };
  
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };
  
  const handleRegenerate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRegenerate) {
      onRegenerate();
    }
  };
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  // Component details dialog content
  const renderDialogContent = () => (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center gap-3 mb-2">
          <div className={`${iconClass} component-icon ${badgeClass} rounded-full h-8 w-8 flex items-center justify-center`}>
            {icon}
          </div>
          <DialogTitle className="text-xl font-bold">
            {component.title} {isContinuation && <span className="text-sm font-normal opacity-70">(continued)</span>}
          </DialogTitle>
          {component.details && typeof component.details === 'object' && (component.details as any).optional && (
            <span className="badge-optional">
              Optional
            </span>
          )}
        </div>
      </DialogHeader>

      {component.description && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-500 mb-1">Description</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300">{component.description}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon className={`h-4 w-4 ${iconClass}`} />
            <span className="font-semibold">{component.type === "hotel" ? "Check-in Date:" : "Start:"}</span> 
            <span className="text-gray-700 dark:text-gray-300">{format(new Date(component.startDate), "EEEE, MMMM d, yyyy")}</span>
          </div>
          {component.endDate && (
            <div className="flex items-center gap-2 text-sm mt-1">
              <CalendarIcon className={`h-4 w-4 ${iconClass}`} />
              <span className="font-semibold">{component.type === "hotel" ? "Check-out Date:" : "End:"}</span> 
              <span className="text-gray-700 dark:text-gray-300">{format(new Date(component.endDate), "EEEE, MMMM d, yyyy")}</span>
            </div>
          )}
        </div>
        
        <div>
          {component.startTime && (
            <div className="flex items-center gap-2 text-sm">
              <ClockIcon className={`h-4 w-4 ${iconClass}`} />
              <span className="font-semibold">{component.type === "hotel" ? "Check-in Time:" : "Time:"}</span> 
              <span className="text-gray-700 dark:text-gray-300">{getTimeDisplay()}</span>
            </div>
          )}
          {component.location && (
            <div className="flex items-center gap-2 text-sm mt-1">
              <MapPinIcon className={`h-4 w-4 ${iconClass}`} />
              <span className="text-gray-700 dark:text-gray-300">{component.location}</span>
            </div>
          )}
        </div>
      </div>
      
      {isContinuation && (
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mb-4 border border-gray-100 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 italic">
            This is a continuation of a multi-day component that began on a previous day.
          </p>
        </div>
      )}
      
      {/* We'll extract details from the component details field if available */}
      {(() => {
        // Safely access the details object
        if (component.details && typeof component.details === 'object') {
          const details = component.details as Record<string, any>;
          
          return (
            <>
              {details.confirmationNumber && (
                <div className="text-sm mb-3">
                  <span className="font-semibold">Confirmation #:</span> 
                  <span className="text-gray-700 dark:text-gray-300 ml-1">{details.confirmationNumber.toString()}</span>
                </div>
              )}
              
              {details.notes && (
                <div className="mb-3">
                  <h4 className="text-sm font-semibold text-gray-500 mb-1">Notes</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{details.notes.toString()}</p>
                </div>
              )}
            </>
          );
        }
        return null;
      })()}

      <DialogFooter className="flex justify-between mt-4">
        <div className="flex gap-2">
          {!isContinuation && onRegenerate && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleRegenerate}
              className="h-9 px-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 text-blue-600 hover:from-blue-500/20 hover:to-cyan-500/20 hover:text-blue-700 rounded-full"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Regenerate
            </Button>
          )}
          
          {!isContinuation && onDelete && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleDelete}
              className="h-9 px-4 bg-gradient-to-r from-red-500/10 to-rose-500/10 text-red-600 hover:from-red-500/20 hover:to-rose-500/20 hover:text-red-700 rounded-full"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          )}
        </div>
        
        <div className="flex gap-2">
          <DialogClose asChild>
            <Button 
              size="sm" 
              variant="outline" 
              className="h-9 px-4 rounded-full bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Close
            </Button>
          </DialogClose>
          
          {!isContinuation && onEdit && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleEdit}
              className="h-9 px-4 rounded-full bg-gradient-to-r from-gray-50 to-white shadow-sm border border-gray-200 hover:from-gray-100 hover:to-gray-50 transition-all duration-200"
            >
              <PencilIcon className="h-4 w-4 mr-2 text-gray-600" /> Edit
            </Button>
          )}
        </div>
      </DialogFooter>
    </DialogContent>
  );

  // Collapsed view
  if (!isExpanded) {
    // If this is a continuation from another day, show a simplified view
    if (isContinuation) {
      return (
        <>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {renderDialogContent()}
          </Dialog>
        
          <div 
            className={`
              ${borderClass} 
              bg-white/90 dark:bg-gray-800/90 rounded-lg shadow-sm border border-gray-100/80 dark:border-gray-700/80 border-dashed p-3 
              hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer relative group card-hover-effect
              ${isMultiDay && !isLastDay ? "multi-day-component" : ""}
              ${isMultiDay && isLastDay ? "multi-day-component last" : ""}
              border-dashed
            `}
            onClick={() => {
              if (usePopupOnClick) {
                onClick();
              } else {
                setDialogOpen(true);
              }
            }}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium rounded-full flex items-center space-x-1 inline-flex">
                  <span className={`${badgeClass} opacity-80 rounded-full px-2 py-1 flex items-center gap-1`}>
                    <span className={iconClass}>{icon}</span>
                    {label} (continued)
                  </span>
                </span>
              </div>
              <div className="w-6"></div>
            </div>
            <div className="flex items-center gap-1">
              <ArrowDownRightIcon className="h-3 w-3 text-gray-400" />
              <h4 className="font-medium text-md mb-1 text-gray-800 dark:text-gray-100 opacity-80">{component.title}</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 opacity-80">
              {isContinuation ? 'Continuing from previous day' : 
                component.description?.substring(0, 60) + 
                (component.description && component.description.length > 60 ? "..." : "")}
            </p>
          </div>
        </>
      );
    }
    
    // Regular component view
    return (
      <>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {renderDialogContent()}
        </Dialog>
      
        <div 
          className={`
            ${borderClass} 
            bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-3 
            hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer relative group card-hover-effect
            ${isMultiDay && !isLastDay ? "multi-day-component" : ""}
            ${isMultiDay && isLastDay ? "multi-day-component last" : ""}
          `}
          onClick={() => {
            if (usePopupOnClick) {
              onClick();
            } else {
              setDialogOpen(true);
            }
          }}
        >
          {/* Action menu */}
          {(onRegenerate || onDelete || onEdit) && (
            <div 
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" aria-label={`Actions for ${component.title}`} 
                    className="h-7 w-7 rounded-full bg-white dark:bg-gray-700 shadow-sm opacity-90 hover:opacity-100 hover:shadow-md transition-all duration-200"
                  >
                    <MoreVertical className="h-4 w-4 text-gray-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onRegenerate && (
                    <DropdownMenuItem onClick={handleRegenerate} className="cursor-pointer focus:bg-gradient-to-r focus:from-blue-50 focus:to-cyan-50">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-blue-500" />
                        <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent font-medium">Regenerate with AI</span>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {onEdit && (
                    <DropdownMenuItem onClick={handleEdit} className="cursor-pointer focus:bg-gradient-to-r focus:from-gray-50 focus:to-gray-100">
                      <div className="flex items-center gap-2">
                        <PencilIcon className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">Edit</span>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <DropdownMenuItem onClick={handleDelete} className="cursor-pointer focus:bg-gradient-to-r focus:from-red-50 focus:to-rose-50">
                      <div className="flex items-center gap-2">
                        <Trash2 className="h-4 w-4 text-red-500" />
                        <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent font-medium">Delete</span>
                      </div>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-medium rounded-full flex items-center space-x-1 inline-flex">
                  <span className={`${badgeClass} rounded-full px-2 py-1 flex items-center gap-1`}>
                    <span className={iconClass}>{icon}</span>
                    {label}
                  </span>
                </span>
                {/* Display optional badge if activity is optional */}
                {component.details && typeof component.details === 'object' && (component.details as any).optional && (
                  <span className="badge-optional">
                    Optional
                  </span>
                )}
                {component.startTime && (
                  <span className="text-sm font-medium">{getTimeDisplay()}</span>
                )}
              </div>
            </div>
            <div className="w-6"></div> {/* Spacer to ensure the action menu has room */}
          </div>
          <h4 className="font-medium text-md mb-1 text-gray-800 dark:text-gray-100">
            {component.type === "flight" && 
             component.details && 
             typeof component.details === 'object' && 
             'legs' in component.details && 
             Array.isArray(component.details.legs) && 
             component.details.legs[0] ? (
              <>
                {component.details.legs[0].departureAirportCode ? (
                  <span>{component.details.legs[0].departureCity || ""} ({component.details.legs[0].departureAirportCode})</span>
                ) : (
                  <span>{component.details.legs[0].departureAirport?.split("(")?.[0] || ""}</span>
                )}
                {" "}
                <span className="text-gray-500 mx-1">→</span>
                {" "}
                {component.details.legs[0].arrivalAirportCode ? (
                  <span>{component.details.legs[0].arrivalCity || ""} ({component.details.legs[0].arrivalAirportCode})</span>
                ) : (
                  <span>{component.details.legs[0].arrivalAirport?.split("(")?.[0] || ""}</span>
                )}
              </>
            ) : (
              component.title
            )}
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {component.type === "flight" && 
             component.details && 
             typeof component.details === 'object' && 
             'legs' in component.details && 
             Array.isArray(component.details.legs) && 
             component.details.legs[0] && 
             'segments' in component.details.legs[0] && 
             Array.isArray(component.details.legs[0].segments) && 
             component.details.legs[0].segments[0] && 
             'airline' in component.details.legs[0].segments[0] ? (
              <>
                {component.details.legs[0].segments[0].airline}
                {'flightNumber' in component.details.legs[0].segments[0] && component.details.legs[0].segments[0].flightNumber && 
                  ` ${component.details.legs[0].segments[0].flightNumber}`}
                {component.startTime && component.endTime && ` · ${component.startTime} - ${component.endTime}`}
              </>
            ) : (
              <>{component.description?.substring(0, 60)}{component.description && component.description.length > 60 ? "..." : ""}</>
            )}
          </p>
        </div>
      </>
    );
  }
  
  // Legacy Expanded view - this should not be used anymore, but we'll keep it for backward compatibility
  return (
    <div className="expanded-card">
      <div className={`expanded-card-header ${bgClass} cursor-pointer`} onClick={() => setIsExpanded(false)}>
        <div className="flex items-center gap-2">
          <div className={`${iconClass} component-icon ${badgeClass} rounded-full flex items-center justify-center`}>
            {icon}
          </div>
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-lg text-gray-800 dark:text-gray-100">
              {component.type === "flight" && 
              component.details && 
              typeof component.details === 'object' && 
              'legs' in component.details && 
              Array.isArray(component.details.legs) && 
              component.details.legs[0] ? (
                <>
                  {component.details.legs[0].departureAirportCode ? (
                    <span>{component.details.legs[0].departureCity || ""} ({component.details.legs[0].departureAirportCode})</span>
                  ) : (
                    <span>{component.details.legs[0].departureAirport?.split("(")?.[0] || ""}</span>
                  )}
                  {" "}
                  <span className="text-gray-500 mx-1">→</span>
                  {" "}
                  {component.details.legs[0].arrivalAirportCode ? (
                    <span>{component.details.legs[0].arrivalCity || ""} ({component.details.legs[0].arrivalAirportCode})</span>
                  ) : (
                    <span>{component.details.legs[0].arrivalAirport?.split("(")?.[0] || ""}</span>
                  )}
                </>
              ) : (
                component.title
              )}
            </h3>
            {component.details && typeof component.details === 'object' && (component.details as any).optional && (
              <span className="badge-optional">
                Optional
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {onRegenerate && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleRegenerate}
              className="h-8 w-8 p-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 text-blue-600 hover:from-blue-500/20 hover:to-cyan-500/20 hover:text-blue-700 rounded-full"
              title="Regenerate with AI"
            >
              <Sparkles className="h-4 w-4" />
            </Button>
          )}
          
          {onDelete && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleDelete}
              className="h-8 w-8 p-0 bg-gradient-to-r from-red-500/10 to-rose-500/10 text-red-600 hover:from-red-500/20 hover:to-rose-500/20 hover:text-red-700 rounded-full"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleEdit}
            className="h-8 px-3 rounded-full bg-gradient-to-r from-gray-50 to-white shadow-sm border border-gray-200 hover:from-gray-100 hover:to-gray-50 transition-all duration-200"
          >
            <PencilIcon className="h-4 w-4 mr-1 text-gray-600" /> <span className="font-medium">Edit</span>
          </Button>
          
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleExpand}
            className="h-8 w-8 p-0 rounded-full bg-gradient-to-r from-gray-100 to-white shadow-sm hover:from-gray-200 hover:to-gray-100 transition-all duration-200"
          >
            <ChevronUpIcon className="h-4 w-4 text-gray-600" />
          </Button>
        </div>
      </div>
      
      <div className="expanded-card-content">
        {component.description && (
          <div>
            <h4 className="text-sm font-semibold text-gray-500 mb-1">Description</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">{component.description}</p>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm">
              <CalendarIcon className={`h-4 w-4 ${iconClass}`} />
              <span className="font-semibold">{component.type === "hotel" ? "Check-in Date:" : "Start:"}</span> 
              <span className="text-gray-700 dark:text-gray-300">{format(new Date(component.startDate), "EEEE, MMMM d, yyyy")}</span>
            </div>
            {component.endDate && (
              <div className="flex items-center gap-2 text-sm mt-1">
                <CalendarIcon className={`h-4 w-4 ${iconClass}`} />
                <span className="font-semibold">{component.type === "hotel" ? "Check-out Date:" : "End:"}</span> 
                <span className="text-gray-700 dark:text-gray-300">{format(new Date(component.endDate), "EEEE, MMMM d, yyyy")}</span>
              </div>
            )}
          </div>
          
          <div>
            {component.startTime && (
              <div className="flex items-center gap-2 text-sm">
                <ClockIcon className={`h-4 w-4 ${iconClass}`} />
                <span className="font-semibold">{component.type === "hotel" ? "Check-in Time:" : "Time:"}</span> 
                <span className="text-gray-700 dark:text-gray-300">{getTimeDisplay()}</span>
              </div>
            )}
            {component.location && (
              <div className="flex items-center gap-2 text-sm mt-1">
                <MapPinIcon className={`h-4 w-4 ${iconClass}`} />
                <span className="text-gray-700 dark:text-gray-300">{component.location}</span>
              </div>
            )}
          </div>
        </div>
        
        {(() => {
          // Safely access the details object
          if (component.details && typeof component.details === 'object') {
            const details = component.details as Record<string, any>;
            
            return (
              <>
                {/* Flight specific details */}
                {component.type === "flight" && 
                 'legs' in details && 
                 Array.isArray(details.legs) && 
                 details.legs[0] && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-gray-500 mb-2">Flight Details</h4>
                    
                    <div className="text-sm">
                      {'cabinClass' in details && details.cabinClass && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">Cabin:</span> 
                          <span className="text-gray-700 dark:text-gray-300 capitalize">
                            {typeof details.cabinClass === 'string' ? details.cabinClass.replace(/([A-Z])/g, ' $1').trim() : details.cabinClass}
                          </span>
                        </div>
                      )}
                      
                      {'passengers' in details && details.passengers && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">Passengers:</span> 
                          <span className="text-gray-700 dark:text-gray-300">{details.passengers}</span>
                        </div>
                      )}
                      
                      {'segments' in details.legs[0] && 
                       Array.isArray(details.legs[0].segments) && 
                       details.legs[0].segments[0] && 
                       'airline' in details.legs[0].segments[0] && 
                       details.legs[0].segments[0].airline && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">Airline:</span> 
                          <span className="text-gray-700 dark:text-gray-300">
                            {details.legs[0].segments[0].airline}
                            {'flightNumber' in details.legs[0].segments[0] && 
                             details.legs[0].segments[0].flightNumber && 
                             ` #${details.legs[0].segments[0].flightNumber}`}
                          </span>
                        </div>
                      )}
                      
                      {'departureAirport' in details.legs[0] && details.legs[0].departureAirport && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">From:</span> 
                          <span className="text-gray-700 dark:text-gray-300">
                            {'departureCity' in details.legs[0] && details.legs[0].departureCity ? 
                              `${details.legs[0].departureCity} ` : ''}
                            {details.legs[0].departureAirport}
                            {'departureAirportCode' in details.legs[0] && 
                             details.legs[0].departureAirportCode && 
                             ` (${details.legs[0].departureAirportCode})`}
                          </span>
                        </div>
                      )}
                      
                      {'arrivalAirport' in details.legs[0] && details.legs[0].arrivalAirport && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">To:</span> 
                          <span className="text-gray-700 dark:text-gray-300">
                            {'arrivalCity' in details.legs[0] && details.legs[0].arrivalCity ? 
                              `${details.legs[0].arrivalCity} ` : ''}
                            {details.legs[0].arrivalAirport}
                            {'arrivalAirportCode' in details.legs[0] && 
                             details.legs[0].arrivalAirportCode && 
                             ` (${details.legs[0].arrivalAirportCode})`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {details.confirmationNumber && (
                  <div className="text-sm mt-2">
                    <span className="font-semibold">Confirmation #:</span> 
                    <span className="text-gray-700 dark:text-gray-300 ml-1">{details.confirmationNumber.toString()}</span>
                  </div>
                )}
                
                {details.notes && (
                  <div className="mt-2">
                    <h4 className="text-sm font-semibold text-gray-500 mb-1">Notes</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{details.notes.toString()}</p>
                  </div>
                )}
              </>
            );
          }
          return null;
        })()}
      </div>
    </div>
  );
}
